import { ErrorHandler, Options, request, SuccessHandler } from 'api/apiTS';
import { mapFiltersToValues } from 'components/common/Search/SearchTopCategories/SearchTopCategoriesModel';
import { baseApiLegacy, baseApiSearch } from 'components/constants/Constants';
import { useShouldLogEvents } from 'hooks/useUserState_utils';
import {
  SearchFilter,
  SearchRequest,
  SearchResults,
  SearchSort,
  SearchSortField,
  SearchSource,
  SearchTarget,
  SortDirection
} from 'types/Search';

export const downloadSearchAsset = (
  assetKey: string,
  success: SuccessHandler,
  handleError: ErrorHandler
) => {
  const options: Options = {
    method: 'GET',
    url: `${baseApiSearch}/public/assets/${assetKey}/download`,
    responseType: 'blob'
  };

  request(options, success, handleError);
};

export const generateRandomSortSeed = () => {
  const randomValue = Math.floor(Math.random() * 99999);
  return 'random_' + randomValue;
};

//this fields are the ones to be or-ed in the backend (send like this {
//   "field": "companies",
//   "value": [
//     "3QP",
//     "3V Sigma",
//     "AAK - Personal Care Division",
//     "AOT"
//   ]
// })//
const isOrField = (field: string) =>
  [
    'company_label',
    'product_label',
    'companies',
    'countries',
    'launch_year',
    'regions',
    'category',
    'trends',
    'award_year',
    'award_category_key',
    'events',
    'document_types',
    'supplier_type'
  ].includes(field);

type SearchRequestPayload = {
  target: string;
  size: number;
  query: string;
  segment: string;
  sort: SearchSort[];
  filters: any[];
  event?: string;
  search_after?: number;
  from?: number;
};

export const Search = (
  searchData: SearchRequest,
  selectedSearchSource: SearchSource | null,
  success: SuccessHandler<SearchResults>,
  error?: ErrorHandler,
  keysOfAdditionalOrFilters?: string[]
) => {
  const data: SearchRequestPayload = {
    target: searchData.target,
    size: searchData.size,
    query: searchData.query,
    segment: searchData.segment,
    sort: searchData.sort ?? [],
    filters: generateFilterPayload(
      searchData.filters.filter(f => f.field !== 'events'),
      keysOfAdditionalOrFilters
    ),
    from: searchData.from
  };

  const eventsFilter = searchData.filters.find(f => f.field === 'events');
  if (eventsFilter) {
    data.event = eventsFilter.value;
  }
  if (searchData.eventKey) {
    data.event = searchData.eventKey;
  }

  if (searchData.query.length > 0) {
    data.sort = [
      {
        field: SearchSortField.SCORE,
        direction: SortDirection.DESCENDENT
      }
    ];
  }
  const options = {
    method: 'POST',
    url: `${baseApiSearch}/search/facet${
      selectedSearchSource === SearchSource.BOOKMARKS ? '?bookmarks=true' : ''
    }`,
    headers: { 'Content-Type': 'application/json' },
    data: JSON.stringify(data)
  };
  request(options, success, error);
};

export const SuppliersSearch = (
  searchData: Omit<SearchRequest, 'target'>,
  selectedSearchSource: SearchSource | null,
  success: SuccessHandler<SearchResults>,
  error?: ErrorHandler
) => {
  const data: Omit<SearchRequestPayload, 'target'> = {
    size: searchData.size,
    query: searchData.query,
    segment: searchData.segment,
    sort: searchData.sort ?? [],
    filters: generateFilterPayload(
      searchData.filters.filter(f => f.field !== 'events')
    ),
    from: searchData.from
  };

  if (searchData.eventKey) {
    data.event = searchData.eventKey;
  }

  if (searchData.query.length > 0) {
    data.sort = [
      {
        field: SearchSortField.SCORE,
        direction: SortDirection.DESCENDENT
      }
    ];
  }
  const options = {
    method: 'POST',
    url: `${baseApiSearch}/search/supplier/facet`,
    headers: { 'Content-Type': 'application/json' },
    ...(selectedSearchSource === SearchSource.BOOKMARKS
      ? { params: { bookmarks: true } }
      : {}),
    data: JSON.stringify(data)
  };
  request(options, success, error);
};

export const generateFilterPayload = (
  filters: SearchFilter[],
  keysOfAdditionalOrFilters?: string[]
) => {
  const filterPayload = filters.reduce<
    { field: string; value: string | string[] | boolean }[]
  >((result, filter) => {
    if (result) {
      if (
        isOrField(filter.field) ||
        keysOfAdditionalOrFilters?.includes(filter.field)
      ) {
        const filterValue = mapFiltersToValues(filter);
        const orFilter = result.find(f => f.field === filter.field);
        if (orFilter) {
          (orFilter.value as string[]).push(...filterValue);
        } else {
          result.push({
            field: filter.field,
            value: filterValue
          });
        }
      } else {
        result.push({ field: filter.field, value: filter.value });
      }
    }
    return result;
  }, []);

  const canSearchByAwards = filters.some(obj => obj.context === 'award');
  if (canSearchByAwards) {
    filterPayload.push({
      field: 'award_shortlisted',
      value: true
    });
  }
  return filterPayload;
};

export const GetSearchSuggestions = (
  searchSuggestionsRequest: any,
  success: SuccessHandler,
  error: ErrorHandler
) => {
  const options = {
    method: 'POST',
    url: `${baseApiSearch}/search/suggestion`,
    headers: { 'Content-Type': 'application/json' },
    data: searchSuggestionsRequest
  };
  request(options, success, error);
};

export const useLogSearchRecord = () => {
  const shouldLogEvent = useShouldLogEvents();

  const logSearchRecord = (
    query: string | undefined,
    recordType: string,
    success: SuccessHandler,
    error?: ErrorHandler
  ) => {
    if (!shouldLogEvent) return;
    const data = {
      searchRecordType: recordType,
      searchValue: query
    };
    const options = {
      method: 'POST',
      url: `${baseApiLegacy}/events/logSearchRecord`,
      headers: { 'Content-Type': 'application/json' },
      data: data
    };

    request(options, success, error);
  };

  return logSearchRecord;
};

export const getUserSearchHistory = (
  target: SearchTarget,
  success: SuccessHandler,
  error: ErrorHandler
) => {
  const options = {
    method: 'GET',
    url: `${baseApiLegacy}/${target.toLowerCase()}/searchHistory`,
    headers: { 'Content-Type': 'application/json' }
  };

  request(options, success, error);
};
